import { useDispatch } from "react-redux";
import { AppDispatch, useSelector } from "@/core/redux/store";
import { ListFilter, Maximize2, Search } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import { useState, useMemo, useEffect } from "react";
import { Input } from "@/shared/components/ui/input";
import {
  type ActivityType,
} from "./mock-data";
import { getLastActivities } from "@/core/redux/thunks/dashboardThunk";
import { IGetLastActivitiesResponse } from "@/shared/services/interface/responses/dashboard";
import TimeGroupItems from "./time-group-items/time-group-items";
import LatestActivitiesLoadingState from "./loading-state";
import { Skeleton } from "@/shared/components/ui/skeleton";

export default function LatestActivities() {
  const lastActivities = useSelector((state) => state.dashboard.lastActivities);
  const isLoading = useSelector((state) => state.dashboard.loadingLastActivities);
  const dispatch = useDispatch<AppDispatch>();
  const userDetail = useSelector((state) => state.user.me);
  const company = userDetail?.company;
  const [activeTab, setActiveTab] = useState<ActivityType>("all");
  const filterParams = useSelector((state) => state.dashboard.filterParams);
  useEffect(() => {
    if (!company?.id) return;
    dispatch(getLastActivities({ companyId: company.id, ...filterParams }));
  }, [company?.id, filterParams]);

  const timeGroupedData = useMemo(() => {
    const now = new Date();

    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfYesterday = new Date(startOfToday);
    startOfYesterday.setDate(startOfYesterday.getDate() - 1);

    const startOfLastWeek = new Date(startOfToday);
    startOfLastWeek.setDate(startOfLastWeek.getDate() - 7);

    const grouped = {
      today: [] as IGetLastActivitiesResponse,
      yesterday: [] as IGetLastActivitiesResponse,
      lastWeek: [] as IGetLastActivitiesResponse,
      inThePast: [] as IGetLastActivitiesResponse,
    };

    lastActivities?.forEach((activity) => {

      const date = new Date(activity.createdAt);

      if (date >= startOfToday) {
        grouped.today.push(activity);
      } else if (date >= startOfYesterday) {
        grouped.yesterday.push(activity);
      } else if (date >= startOfLastWeek) {
        grouped.lastWeek.push(activity);
      } else {
        grouped.inThePast.push(activity);
      }
    })

    return grouped;
  }, [lastActivities]);

  const totalTodayActivities = timeGroupedData.today.length;
  const totalYesterdayActivities = timeGroupedData.yesterday.length;
  const totalLastWeekActivities = timeGroupedData.lastWeek.length;
  const totalInThePastActivities = timeGroupedData.inThePast.length;

  const totalActivities = totalTodayActivities + totalYesterdayActivities + totalLastWeekActivities + totalInThePastActivities;

  return (
    <div className="w-full h-full rounded-[16px] border border-[#e5e5e5] bg-white">
      <div className="py-[16px] px-[20px] border-b border-[#e5e5e5]">
        <div className="flex items-center justify-between">
          <div className="text-[16px] font-[700] text-foreground leading-[24px]">
            Son Aktiviteler
          </div>
          <div className="flex items-center justify-end gap-[8px]">
            <button className="size-[28px] hover:bg-[#F4F4F5] rounded-[6px] flex items-center justify-center">
              <Maximize2 size={16} color="#71717A" />
            </button>
            <button className="size-[28px] hover:bg-[#F4F4F5] rounded-[6px] flex items-center justify-center">
              <ListFilter size={16} color="#71717A" />
            </button>
          </div>
        </div>
        <div className="text-[14px] font-[600] text-muted-foreground mt-[2px]">
          {
            isLoading ? <Skeleton className="w-[100px] h-[20px] mt-[4px]" /> : `${totalActivities} new activities`
          }
        </div>
        <Tabs
          value={activeTab}
          onValueChange={(value) => setActiveTab(value as ActivityType)}
          className="mt-[16px]"
        >
          <TabsList className="inline-flex flex-wrap gap-[10px] h-auto bg-transparent p-0">
            <TabsTrigger
              value="all"
              className="h-[28px] px-[10px] py-0 rounded-[8px] text-sm leading-[20px] !font-[600] data-[state=active]:bg-[rgba(9,9,11,0.1)] data-[state=active]:text-foreground data-[state=inactive]:bg-[rgba(228,228,231,0.4)] data-[state=inactive]:text-muted-foreground"
            >
              Tümü
            </TabsTrigger>
            <TabsTrigger
              value="stakeholder"
              className="h-[28px] px-[10px] py-0 rounded-[8px] text-sm leading-[20px] !font-[600] data-[state=active]:bg-[rgba(9,9,11,0.1)] data-[state=active]:text-foreground data-[state=inactive]:bg-[rgba(228,228,231,0.4)] data-[state=inactive]:text-muted-foreground"
            >
              Paydaş
            </TabsTrigger>
            <TabsTrigger
              value="supplier"
              className="h-[28px] px-[10px] py-0 rounded-[8px] text-sm leading-[20px] !font-[600] data-[state=active]:bg-[rgba(9,9,11,0.1)] data-[state=active]:text-foreground data-[state=inactive]:bg-[rgba(228,228,231,0.4)] data-[state=inactive]:text-muted-foreground"
            >
              Tedarikçi
            </TabsTrigger>
          </TabsList>
        </Tabs>
        {/* search input component */}
      </div>
      <div className="relative border-b border-[#e5e5e5]">
        <Search
          size={12}
          className="text-foreground absolute left-[25px] top-[16px]"
        />
        <Input
          placeholder="Search"
          className="h-[48px] pl-[50px] !border-none !rounded-none !hover:outline-none !focus:outline-none !hover:border-none !focus-visible:outline-none !focus-visible:border-none focus-visible:ring-0 focus-visible:ring-offset-0"
        />
      </div>
      <div className="h-[477px] max-h-[477px] overflow-auto">
        <div className="flex flex-col gap-[12px] items-start p-[20px]">
          {
            totalTodayActivities > 0 && !isLoading && (
              <TimeGroupItems groupLabel="Today" groupData={timeGroupedData.today} />
            )
          }
          {
            totalYesterdayActivities > 0 && !isLoading && (
              <TimeGroupItems groupLabel="Yesterday" groupData={timeGroupedData.yesterday} />
            )
          }
          {
            totalLastWeekActivities > 0 && !isLoading && (
              <TimeGroupItems groupLabel="Last Week" groupData={timeGroupedData.lastWeek} />
            )
          }
          {
            totalInThePastActivities > 0 && !isLoading && (
              <TimeGroupItems groupLabel="In The Past" groupData={timeGroupedData.inThePast} />
            )
          }
          {
            !totalActivities && !isLoading && (
              <div className="flex items-center justify-center h-full w-full">
                <div className="text-[14px] font-[600] text-muted-foreground text-center w-full">
                  No activities found
                </div>
              </div>
            )
          }
          {
            isLoading && (
              <LatestActivitiesLoadingState />
            )
          }
        </div>
      </div>
    </div>
  );
}
